import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ONBOARDING } from "@/constants/testIds";
import SignatureSoundControl from "@/components/SignatureSoundControl";

const RHYTHMS = ["A gentle nudge a few times a week", "A daily reflection", "Just when I open the app"];
const COLLECTIONS = [
  { id: "mena-women", label: "MENA Women", note: "Middle Eastern & North African identity, family, faith & becoming" },
  { id: "black-women", label: "Black Women", note: "Strength & softness, beauty, joy, healing & becoming" },
  { id: "latina-women", label: "Latina Women", note: "Marianismo, familia, ambition, faith & becoming" },
  { id: "south-asian-women", label: "South Asian Women", note: "The good daughter, marriage, expectation, faith & becoming" },
  { id: "caribbean-women", label: "Caribbean Women", note: "Strong-woman armour, shadeism, respectability, carnival & becoming" },
  { id: "east-asian-women", label: "East Asian Women", note: "Model-minority mask, filial duty, face, voice & becoming" },
  { id: "african-women", label: "African Women", note: "Strength, family, tradition, skin & hair, voice & roots" },
  { id: "indigenous-women", label: "Indigenous Women", note: "Identity, historical trauma, land, ancestors & becoming" },
  { id: "pacific-islander-women", label: "Pacific Islander Women", note: "Islands & diaspora, aiga, body, culture & becoming" },
  { id: "mixed-race-women", label: "Mixed-Race Women", note: "Belonging, claiming all of you, family & wholeness" },
  { id: "immigrant-women", label: "Immigrant & First-Gen Women", note: "Two worlds, sacrifice, belonging & success guilt" },
  { id: "jewish-women", label: "Jewish Women", note: "Peoplehood, inherited trauma, expectation, faith & joy" },
  { id: "all", label: "Show me everything", note: "I'd love to explore all of the journeys" },
  { id: "prefer-not", label: "Prefer not to say", note: "Skip this — just show me the full library" },
];
const SCALE = [
  { v: 0, label: "Not at all" },
  { v: 1, label: "Several days" },
  { v: 2, label: "More than half the days" },
  { v: 3, label: "Nearly every day" },
];

export default function Onboarding() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const preAuth = !user; // pre-auth "Get Started" funnel when no user (public /get-started)
  const [meta, setMeta] = useState({ goals: [], life_stages: [] });
  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState([]);
  const [stage, setStage] = useState(null);
  const [rhythm, setRhythm] = useState(null);
  const [faith, setFaith] = useState(false);
  const [collection, setCollection] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  // phase: "questions" -> "assessment" -> "score"
  const [phase, setPhase] = useState("questions");
  const [flat, setFlat] = useState([]);
  const [assessTitle, setAssessTitle] = useState("Women's Wellness Check-In");
  const [aIndex, setAIndex] = useState(0);
  const [aAnswers, setAAnswers] = useState({});
  const [aAdvancing, setAAdvancing] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    track("onboarding_started");
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.onboarding_completed) navigate("/home");
  }, [user, navigate]);

  const toggleGoal = (id) => {
    setGoals((g) => (g.includes(id) ? g.filter((x) => x !== id) : g.length < 5 ? [...g, id] : g));
  };

  // Save preferences, then move into the required wellness check-in
  // — unless she already took the free check-in (landing) or one exists this month.
  const startAssessment = async () => {
    setSaving(true);
    try {
      const prefs = { goals, life_stage: stage, reflection_rhythm: rhythm, faith_enabled: faith, preferred_collection: collection };
      if (preAuth) {
        // No account yet — stash preferences; they're saved on sign-in.
        sessionStorage.setItem("sh_prefs", JSON.stringify(prefs));
      } else {
        const { data } = await api.put("/profile", prefs);
        setUser(data);
        // Prefill 1: the free landing check-in (full result incl. narrative)
        const stored = sessionStorage.getItem("sh_last_result");
        if (stored) {
          try {
            const r = JSON.parse(stored);
            await api.post("/assessment/save", r).catch(() => {});
            sessionStorage.removeItem("sh_last_result");
            setResult(r);
            setPhase("score");
            track("onboarding_assessment_prefilled", { source: "landing" });
            return;
          } catch (e) { console.debug("onboarding prefill failed:", e); /* fall through */ }
        }
        // Prefill 2: an already-saved check-in from this month
        const st = await api.get("/assessment/status").then((r) => r.data).catch(() => null);
        if (st?.latest && !st.can_retake) {
          const lr = st.latest;
          setResult({
            wellness_score: lr.wellness_score, domain_scores: lr.domain_scores || {},
            top_concern: lr.top_concern || {}, strength: lr.strength || {},
            recommended_plan_id: lr.recommended_plan_id, narrative: lr.narrative,
          });
          setPhase("score");
          track("onboarding_assessment_prefilled", { source: "account" });
          return;
        }
      }
      // Run the full check-in (questions endpoint is public)
      const { data: q } = await api.get("/assessment/questions");
      const list = [];
      q.domains.forEach((d) => d.questions.forEach((qq) => list.push({ ...qq, domain: d.name })));
      setFlat(list);
      setAssessTitle(q.title);
      setPhase("assessment");
      track("onboarding_assessment_started");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const submitAssessment = async (finalAnswers) => {
    setScoring(true);
    try {
      const { data } = await api.post("/assessment/submit", { answers: finalAnswers });
      if (preAuth) sessionStorage.setItem("sh_last_result", JSON.stringify(data));
      else await api.post("/assessment/save", data).catch(() => {}); // 409 if already saved this month — fine
      setResult(data);
      setPhase("score");
      track("onboarding_assessment_completed", { wellness_score: data.wellness_score });
    } catch {
      toast.error("Could not score your check-in. Please try again.");
      setScoring(false);
      setAAdvancing(false);
    }
  };

  const pickAnswer = (v) => {
    if (aAdvancing || scoring) return;
    const q = flat[aIndex];
    const next = { ...aAnswers, [q.id]: v };
    setAAnswers(next);
    setAAdvancing(true);
    setTimeout(() => {
      if (aIndex < flat.length - 1) { setAIndex(aIndex + 1); setAAdvancing(false); }
      else submitAssessment(next);
    }, 240);
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { onboarding_completed: true });
      setUser(data);
      localStorage.removeItem("sh_tour_completed"); // guarantee the guided tour shows on first login
      track("onboarding_completed", { goals: goals.length });
      let entitled = data.entitled ?? (data.is_premium || data.role === "owner");
      // Auto-start the 7-day free trial so new members land straight in the app
      // (instead of hitting the paywall). Falls back to /paywall if a trial can't start.
      if (!entitled && !data.trial_started_at) {
        try {
          const { data: trial } = await api.post("/billing/start-trial");
          track("free_trial_started", { source: "onboarding" });
          setUser((u) => ({ ...(u || {}), is_trial_active: true, entitled: true, access: trial.access }));
          await checkAuth();
          entitled = true;
          toast.success("Your 7-day free trial is active. Welcome to Sisterhood+.");
        } catch (e) {
          console.debug("auto trial start failed:", e);
        }
      }
      navigate(entitled ? "/home" : "/paywall");
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    {
      title: "Welcome to Sisterhood+",
      subtitle: "A quick understanding before you begin.",
      canNext: agreed,
      body: (
        <div className="space-y-4">
          <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-5 text-sm leading-relaxed text-[#59535B] space-y-3 max-h-72 overflow-y-auto">
            <p>Sisterhood+ is a personal-growth and wellness space for women from all backgrounds. It offers lessons, journaling, wellness check-ins, a warm AI Coach, and a moderated community of sisters — with experiences for what we share and culturally responsive spaces for the stories that make us different.</p>
            <p><strong>Please understand:</strong> Sisterhood+ and its AI Coach provide supportive guidance and reflection. They are <strong>not</strong> therapy, medical, mental-health, legal, or financial advice, and not a crisis service.</p>
            <p>If you are ever in danger or thinking about harming yourself, call or text <a href="tel:988" className="text-[#291B2D] underline font-medium">988</a> (Suicide &amp; Crisis Lifeline), or call <a href="tel:911" className="text-[#291B2D] underline font-medium">911</a> in an emergency.</p>
            <p>Your journal and coaching conversations are private and are never used for analytics or sold.</p>
          </div>
          <button data-testid="onboarding-consent-checkbox" onClick={() => setAgreed((a) => !a)}
            className="w-full flex items-center gap-3 text-left px-5 py-4 rounded-xl border transition-all bg-white border-[#E5DED4] hover:border-[#C89B55]">
            <span className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${agreed ? "bg-[#C89B55] border-[#C89B55]" : "border-[#D6CFC7]"}`}>
              {agreed && <Check className="h-4 w-4 text-[#291B2D]" />}
            </span>
            <span className="text-sm">I've read and understand the above, and I agree to use Sisterhood+ as a growth and wellness space.</span>
          </button>
        </div>
      ),
    },
    {
      title: "What would you like to work on?",
      subtitle: "Choose 3–5 areas. You can change these anytime.",
      canNext: goals.length >= 3,
      body: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {meta.goals.map((g) => {
            const active = goals.includes(g.id);
            return (
              <button key={g.id} data-testid={`${ONBOARDING.goalOption}-${g.id}`} onClick={() => toggleGoal(g.id)}
                className={`flex items-center justify-between text-left px-5 py-4 rounded-xl border transition-all ${
                  active ? "bg-[#291B2D] text-[#FCFAF6] border-[#291B2D]" : "bg-[#F7F2EA] border-[#E5DED4] hover:border-[#C89B55]"
                }`}>
                <span className="font-medium">{g.label}</span>
                {active && <Check className="h-5 w-5 text-[#C89B55]" />}
              </button>
            );
          })}
        </div>
      ),
    },
    {
      title: "What season of life are you in?",
      subtitle: "This helps us make things relevant to you.",
      canNext: !!stage,
      body: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {meta.life_stages.map((s) => (
            <button key={s} data-testid={ONBOARDING.stageOption} onClick={() => setStage(s)}
              className={`text-left px-5 py-4 rounded-xl border transition-all ${
                stage === s ? "bg-[#291B2D] text-[#FCFAF6] border-[#291B2D]" : "bg-[#F7F2EA] border-[#E5DED4] hover:border-[#C89B55]"
              }`}>
              {s}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "Want content that speaks to a particular community?",
      subtitle: "Optional. Used to personalize what we show you first — never to decide whether you belong. You'll always have the full library, and you can change this anytime.",
      canNext: true,
      body: (
        <div className="space-y-3">
          {COLLECTIONS.map((c) => (
            <button key={c.id} data-testid={`onboarding-collection-${c.id}`} onClick={() => setCollection(collection === c.id ? null : c.id)}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                collection === c.id ? "bg-[#291B2D] text-[#FCFAF6] border-[#291B2D]" : "bg-[#F7F2EA] border-[#E5DED4] hover:border-[#C89B55]"
              }`}>
              <span className="font-medium block">{c.label}</span>
              <span className={`text-sm ${collection === c.id ? "text-[#C89B55]" : "opacity-70"}`}>{c.note}</span>
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "How often would you like to reflect?",
      subtitle: "We'll never spam you — only genuinely useful nudges.",
      canNext: !!rhythm,
      body: (
        <div className="space-y-3">
          {RHYTHMS.map((r) => (
            <button key={r} data-testid={ONBOARDING.rhythmOption} onClick={() => setRhythm(r)}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                rhythm === r ? "bg-[#291B2D] text-[#FCFAF6] border-[#291B2D]" : "bg-[#F7F2EA] border-[#E5DED4] hover:border-[#C89B55]"
              }`}>
              {r}
            </button>
          ))}
          <button data-testid={ONBOARDING.faithToggle} onClick={() => setFaith((f) => !f)}
            className={`w-full flex items-center justify-between text-left px-5 py-4 rounded-xl border transition-all mt-4 ${
              faith ? "bg-[#352238] text-[#FCFAF6] border-[#C89B55]" : "bg-[#F7F2EA] border-[#E5DED4]"
            }`}>
            <span>
              <span className="font-medium block">Include faith & spirituality</span>
              <span className="text-sm opacity-80">Optional — off by default</span>
            </span>
            <span className={`h-6 w-11 rounded-full flex items-center px-0.5 transition-all ${faith ? "bg-[#C89B55]" : "bg-[#D6CFC7]"}`}>
              <span className={`h-5 w-5 rounded-full bg-white transition-all ${faith ? "translate-x-5" : ""}`} />
            </span>
          </button>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const questionsProgress = ((step + 1) / steps.length) * 100;
  const assessProgress = flat.length ? ((aIndex + (aAnswers[flat[aIndex]?.id] !== undefined ? 1 : 0)) / flat.length) * 100 : 0;
  const progress = phase === "questions" ? questionsProgress : phase === "assessment" ? assessProgress : 100;

  return (
    <div className="min-h-screen bg-[#FCFAF6] px-6 py-10 lg:py-16">
      <SignatureSoundControl />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <span className="font-display text-xl">Sisterhood<span className="text-[#C89B55]">+</span></span>
          {phase === "questions" && (
            <button data-testid={ONBOARDING.skipButton} onClick={startAssessment} disabled={saving}
              className="text-sm text-[#8A838E] hover:text-[#252326]">
              Skip for now
            </button>
          )}
        </div>

        <div className="h-1.5 bg-[#E5DED4] rounded-full mb-10 overflow-hidden">
          <motion.div className="h-full bg-[#C89B55]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>

        {/* PHASE 1 — preference questions */}
        {phase === "questions" && (
          <>
            <AnimatePresence mode="wait">
              <motion.div key={step}
                initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}>
                <h1 className="font-display text-3xl lg:text-4xl font-semibold">{current.title}</h1>
                <p className="text-[#59535B] mt-2 mb-8">{current.subtitle}</p>
                {current.body}
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-10">
              <button data-testid={ONBOARDING.backButton} onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="px-5 py-3 rounded-full text-[#252326] hover:bg-[#F7F2EA] disabled:opacity-40 transition-all">
                Back
              </button>
              {step < steps.length - 1 ? (
                <button data-testid={ONBOARDING.nextButton} onClick={() => setStep((s) => s + 1)} disabled={!current.canNext}
                  className="px-8 py-3.5 rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] disabled:opacity-40 transition-all font-medium">
                  Continue
                </button>
              ) : (
                <button data-testid="onboarding-to-assessment" onClick={startAssessment} disabled={!current.canNext || saving}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] disabled:opacity-40 transition-all font-medium">
                  {saving ? "One moment…" : <>Continue to your check-in <ArrowRight size={16} /></>}
                </button>
              )}
            </div>
          </>
        )}

        {/* PHASE 2 — required wellness check-in */}
        {phase === "assessment" && flat.length > 0 && (
          <div data-testid="onboarding-assessment">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-1">{assessTitle}</p>
            <p className="text-sm text-[#291B2D]/50 mb-6">{aIndex + 1} of {flat.length}</p>
            <div className="bg-white rounded-3xl p-8 lg:p-12 card-soft animate-fade-up" key={aIndex}>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-4">{flat[aIndex].domain}</p>
              <h2 className="font-display text-2xl lg:text-3xl leading-snug mb-2">In the past two weeks, how often have you:</h2>
              <p className="text-lg lg:text-xl text-[#291B2D]/80 mb-8 lg:mb-10">{flat[aIndex].text}</p>
              <div className="space-y-2 lg:space-y-3">
                {SCALE.map((s) => (
                  <button key={s.v} data-testid={`onboarding-assessment-answer-${flat[aIndex].id}-${s.v}`} onClick={() => pickAnswer(s.v)} disabled={scoring || aAdvancing}
                    className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-smooth text-sm lg:text-base ${aAnswers[flat[aIndex].id] === s.v ? "bg-[#291B2D] text-white border-[#291B2D]" : "bg-[#F7F2EA] border-transparent hover:bg-[#EFE7DA]"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="mt-8 flex items-center justify-between gap-3">
                {aIndex > 0 ? (
                  <button data-testid="onboarding-assessment-back" onClick={() => { if (!aAdvancing && !scoring) setAIndex(aIndex - 1); }} className="text-sm text-[#291B2D]/50 hover:text-[#291B2D] px-2 py-3">← Back</button>
                ) : <span />}
                {scoring
                  ? <span className="text-sm text-[#291B2D]/50" data-testid="onboarding-assessment-scoring">Scoring…</span>
                  : <span className="text-xs text-[#291B2D]/40">Tap an answer to continue</span>}
              </div>
            </div>
            <p className="text-xs text-[#291B2D]/50 mt-4 text-center">Sisterhood+ is not therapy and does not diagnose.</p>
          </div>
        )}

        {/* PHASE 3 — wellness score */}
        {phase === "score" && result && (
          <div data-testid="onboarding-score" className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">Your snapshot</p>
            <h1 className="font-display text-4xl mb-2">Here's where you are.</h1>
            <p className="text-[#291B2D]/60 mb-6">A starting point, sister — not a verdict.</p>

            <div data-testid="onboarding-wellness-score" className="bg-[#291B2D] text-white rounded-3xl p-8 mb-6 card-soft">
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">Wellness Score</p>
              <div className="font-display text-6xl font-light">{result.wellness_score}</div>
              <div className="text-white/60 text-sm mt-2">out of 100</div>
            </div>

            {result.narrative && (
              <div data-testid="onboarding-narrative" className="bg-white rounded-3xl p-6 card-soft mb-6 border-l-4 border-[#C89B55]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-2">Your reflection</p>
                <p className="text-[#291B2D]/85 leading-relaxed">{result.narrative}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="bg-white rounded-3xl p-5 card-soft">
                <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Gently nurture</p>
                <p className="font-display text-lg mt-1">{result.top_concern?.name}</p>
              </div>
              <div className="bg-white rounded-3xl p-5 card-soft">
                <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Your strength</p>
                <p className="font-display text-lg mt-1">{result.strength?.name}</p>
              </div>
            </div>

            {preAuth ? (
              <>
                <button data-testid="onboarding-create-account" onClick={() => { track("get_started_to_register"); navigate("/register"); }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] px-8 py-4 font-semibold transition-all">
                  Create your account to save this <ArrowRight size={16} />
                </button>
                <button data-testid="onboarding-signin" onClick={() => navigate("/login")}
                  className="mt-3 w-full text-sm font-medium text-[#291B2D]/60 hover:text-[#291B2D] transition-smooth py-2">
                  Already a member? Sign in
                </button>
              </>
            ) : (
              <button data-testid={ONBOARDING.finishButton} onClick={completeOnboarding} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] disabled:opacity-40 px-8 py-4 font-semibold transition-all">
                {saving ? "Setting up…" : <>Enter Sisterhood+ <ArrowRight size={16} /></>}
              </button>
            )}
            <p className="text-xs text-[#291B2D]/50 mt-4 text-center">You can retake this check-in monthly from Wellness.</p>
          </div>
        )}
      </div>
    </div>
  );
}
